<template>
  <div class="popular-list-container" v-if="plate_code" @scroll="handleScroll">
    <div v-if="loading && stocks.length === 0" class="text-center py-3">
      <b-spinner small variant="primary"></b-spinner>
    </div>
    <div v-else-if="stocks.length === 0" class="text-center py-3 text-muted small" style="font-size: 11px;">
      暂无数据
    </div>
    <div v-else class="popular-items-list">
      <div 
        v-for="item in filteredStocks" 
        :key="item.stock_code" 
        class="popular-item-row"
        :class="getRowHighlightClass(item.stock_name)"
        v-b-toggle.sidebar-right-stock
        @click="handleRowClick(item)"
      >
        <!-- 排名 -->
        <div class="rank-badge" :class="getRankClass(item.rank)">
          {{ item.rank }}
        </div>
        
        <!-- 变化 -->
        <div class="rank-diff" :class="getDiffClass(item.rank_diff)">
          {{ formatDiff(item.rank_diff) }}
        </div>
        
        <!-- 股票名称 -->
        <div class="stock-name" :title="item.stock_name">
          {{ item.stock_name }}
        </div>
        
        <!-- 涨幅 -->
        <div class="stock-pct" :style="getPctStyle(getPctValue(item))">
          {{ formatPct(getPctValue(item)) }}
        </div>
      </div>
      <!-- 加载更多 spinner -->
      <div v-if="loading && stocks.length > 0" class="text-center py-2">
        <b-spinner small variant="secondary"></b-spinner>
      </div>
    </div>
  </div>
</template>

<script>
import { request_zzquant } from "network/request.js"

export default {
  name: "PopularListOnPlate",
  props: {
    plate_code: {
      type: String,
      default: ""
    },
    plate_type: {
      type: Number,
      default: 17
    },
    date1: {
      type: String,
      default: ""
    },
    isLatest: {
      type: Boolean,
      default: false
    },
    highlightStockName: {
      type: String,
      default: null
    },
    limit: {
      type: Number,
      default: 20
    }
  },
  data() {
    return {
      loading: false,
      stocks: [],
      page: 1,
      total: 0,
      changeTimer: null
    };
  },
  computed: {
    filteredStocks() {
      return this.stocks || [];
    }
  },
  watch: {
    plate_code: {
      immediate: true,
      handler() {
        this.onPropsChange();
      }
    },
    plate_type: {
      immediate: true,
      handler() {
        this.onPropsChange();
      }
    },
    date1: {
      immediate: true,
      handler() {
        this.onPropsChange();
      }
    },
    isLatest: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.stocks.length > 0) {
          this.$emit("latest-first-stock", this.stocks[0].stock_name);
        }
      }
    },
    limit: {
      handler() {
        this.onPropsChange();
      }
    }
  },
  beforeDestroy() {
    if (this.changeTimer) {
      clearTimeout(this.changeTimer);
    }
  },
  methods: {
    onPropsChange() {
      if (this.changeTimer) clearTimeout(this.changeTimer);
      this.changeTimer = setTimeout(() => {
        this.fetchPopularStocks(true);
      }, 50);
    },
    fetchPopularStocks(reset = false) {
      if (!this.plate_code || !this.date1) return;
      
      if (reset) {
        this.page = 1;
        this.total = 0;
      }
      
      this.loading = true;
      request_zzquant({
        url: `/v3/market/plates/${this.plate_type}/${this.plate_code}/stocks/rank`,
        params: {
          with_pct: 1,
          limit: this.limit,
          date1: this.date1,
          page: this.page
        }
      })
      .then(res => {
        let list = [];
        let total = 0;
        if (Array.isArray(res.data.data)) {
          list = res.data.data;
          total = list.length;
        } else {
          const resData = res.data.data || {};
          list = resData.list || [];
          total = resData.total || 0;
        }
        
        if (reset) {
          this.stocks = list;
        } else {
          // Append and filter duplicates by stock_code
          const currentCodes = new Set(this.stocks.map(s => s.stock_code));
          const newItems = list.filter(item => !currentCodes.has(item.stock_code));
          this.stocks = [...this.stocks, ...newItems];
        }
        
        
        if (this.isLatest && this.stocks.length > 0) {
          this.$emit("latest-first-stock", this.stocks[0].stock_name);
        }
        this.total = total;
        this.checkAndLoadMore();
      })
      .catch(err => {
        console.error("Fetch popular stocks error:", err);
        if (reset) this.stocks = [];
      })
      .finally(() => {
        this.loading = false;
      });
    },
    checkAndLoadMore() {
      this.$nextTick(() => {
        const container = this.$el;
        if (container && container.scrollHeight > 0 && (container.scrollHeight - container.clientHeight <= 10)) {
          if (!this.loading && this.stocks.length < this.total) {
            this.page += 1;
            this.fetchPopularStocks(false);
          }
        }
      });
    },
    handleScroll(e) {
      const container = e.target;
      if (container.scrollHeight - Math.ceil(container.scrollTop) - container.clientHeight <= 50) {
        if (!this.loading && this.stocks.length < this.total) {
          this.page += 1;
          this.fetchPopularStocks(false);
        }
      }
    },
    getPctValue(item) {
      if (item.px_change_rate !== undefined && item.px_change_rate !== null) {
        return item.px_change_rate;
      }
      return item.last_pct;
    },
    formatPct(val) {
      if (val === undefined || val === null) return "-";
      return (val > 0 ? "+" : "") + val.toFixed(2) + "%";
    },
    formatDiff(diff) {
      if (diff === undefined || diff === null || diff === 0) return "=";
      return diff > 0 ? `+${diff}` : `${diff}`;
    },
    getRankClass(rank) {
      if (rank === 1) return 'rank-1';
      if (rank === 2) return 'rank-2';
      if (rank === 3) return 'rank-3';
      return 'rank-other';
    },
    getDiffClass(diff) {
      if (diff === undefined || diff === null || diff === 0) return "diff-same";
      return diff > 0 ? "diff-up" : "diff-down";
    },
    getPctStyle(val) {
      const v = parseFloat(val);
      if (isNaN(v) || v === 0) return { 'color': 'var(--text-muted, #adbbca)' };
      const isDark = document.body.classList.contains('theme-dark');
      const isUp = v > 0;
      let color;
      if (isUp) {
        color = isDark ? '#ff6b6b' : '#dc3545';
      } else {
        color = isDark ? '#52c41a' : '#28a745';
      }
      return {
        'color': color,
        'font-weight': '600'
      };
    },
    getRowHighlightClass(stockName) {
      if (this.highlightStockName && this.highlightStockName === stockName) {
        return 'highlight-clicked';
      }
      return '';
    },
    handleRowClick(item) {
      this.$emit("stock-clicked", item.stock_name);
      this.$emit("show_sidebar", {
        stock_code: item.stock_code,
        stock_name: item.stock_name,
        date1: this.date1
      });
    }
  }
};
</script>

<style scoped>
.popular-list-container {
  display: flex;
  flex-direction: column;
  background-color: var(--bg-card, #ffffff);
  border: 1px solid var(--border-color, #e9ecef);
  border-radius: 4px;
  padding: 2px;
  min-height: 100px;
  max-height: 700px;
  overflow-y: auto;
}

/* 美化滚动条 */
.popular-list-container::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.popular-list-container::-webkit-scrollbar-track {
  background: transparent;
}

.popular-list-container::-webkit-scrollbar-thumb {
  background-color: rgba(144, 147, 153, 0.3);
  border-radius: 3px;
  transition: background-color 0.3s;
}

.popular-list-container::-webkit-scrollbar-thumb:hover {
  background-color: rgba(144, 147, 153, 0.5);
}

.popular-items-list {
  display: flex;
  flex-direction: column;
}

.popular-item-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 3px 2px;
  cursor: pointer;
  font-size: 11px;
  border-bottom: 1px solid var(--border-color, #f8f9fa);
  transition: background-color 0.15s;
  white-space: nowrap;
  overflow: hidden;
}

.popular-item-row:hover {
  background-color: rgba(0, 123, 255, 0.1);
}

.rank-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  font-size: 9px;
  font-weight: bold;
  border-radius: 2px;
  flex-shrink: 0;
}

.rank-1 {
  background-color: #dc3545;
  color: #fff;
}

.rank-2 {
  background-color: #fd7e14;
  color: #fff;
}

.rank-3 {
  background-color: #ffc107;
  color: #212529;
}

.rank-other {
  background-color: var(--bg-muted, #e9ecef);
  color: var(--text-muted, #6c757d);
}

.rank-diff {
  width: 22px;
  text-align: center;
  font-size: 9px;
  font-weight: bold;
  margin-left: 2px;
  flex-shrink: 0;
  white-space: nowrap;
}

.diff-up {
  color: #dc3545;
}

.diff-down {
  color: #28a745;
}

.diff-same {
  color: var(--text-muted, #6c757d);
}

.stock-name {
  width: 44px;
  flex-shrink: 0;
  text-align: left;
  margin-left: 4px;
  font-weight: 500;
  color: var(--text-main, #212529);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stock-pct {
  width: 44px;
  text-align: left;
  font-size: 10px;
  font-weight: 600;
  flex-shrink: 0;
  white-space: nowrap;
  margin-left: 4px;
}

/* Dark theme overrides */
body.theme-dark .popular-item-row {
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

body.theme-dark .rank-other {
  background-color: #343a40;
  color: #adbbca;
}

body.theme-dark .stock-name {
  color: #ced4da;
}

.highlight-top-1 {
  background-color: rgba(220, 53, 69, 0.12) !important;
  box-shadow: inset 0 0 0 1px rgba(220, 53, 69, 0.35);
  border-radius: 2px;
}
.highlight-top-2 {
  background-color: rgba(253, 126, 20, 0.12) !important;
  box-shadow: inset 0 0 0 1px rgba(253, 126, 20, 0.35);
  border-radius: 2px;
}
.highlight-top-3 {
  background-color: rgba(255, 193, 7, 0.12) !important;
  box-shadow: inset 0 0 0 1px rgba(255, 193, 7, 0.35);
  border-radius: 2px;
}
.highlight-clicked {
  background-color: rgba(111, 66, 193, 0.15) !important;
  box-shadow: inset 0 0 0 1.5px rgba(111, 66, 193, 0.6);
  border-radius: 2px;
}

/* Dark theme overrides */
body.theme-dark .highlight-top-1 {
  background-color: rgba(255, 107, 107, 0.15) !important;
  box-shadow: inset 0 0 0 1px rgba(255, 107, 107, 0.45);
}
body.theme-dark .highlight-top-2 {
  background-color: rgba(253, 126, 20, 0.15) !important;
  box-shadow: inset 0 0 0 1px rgba(253, 126, 20, 0.45);
}
body.theme-dark .highlight-top-3 {
  background-color: rgba(255, 193, 7, 0.15) !important;
  box-shadow: inset 0 0 0 1px rgba(255, 193, 7, 0.45);
}
body.theme-dark .highlight-clicked {
  background-color: rgba(157, 107, 230, 0.2) !important;
  box-shadow: inset 0 0 0 1.5px rgba(157, 107, 230, 0.7);
}

@media (max-width: 768px) {
  .popular-item-row {
    font-size: 10px;
    padding: 3px 1px;
  }
  .rank-badge {
    width: 12px;
    height: 12px;
    font-size: 8px;
  }
  .rank-diff {
    width: 16px;
    font-size: 8px;
    margin-left: 1px;
  }
  .stock-pct {
    width: 40px;
    font-size: 9px;
    margin-left: 2px;
  }
  .stock-name {
    width: 50px;
    margin-left: 2px;
  }
}
</style>
